import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardMetricsTable, {
  DashboardMetricsTableContext,
  DashboardMetricsTableRow,
} from "../../components/dashboard/DashboardMetricsTable";
import ComplianceTable from "../../components/dashboard/ComplianceTable";
import FinancialInformationTable from "../../components/dashboard/FinancialInformationTable";
import MaterialsRecoveredTable from "../../components/dashboard/MaterialsRecoveredTable";
import PartsManagementTable from "../../components/dashboard/PartsManagementTable";
import PartsUserActivityTable from "../../components/shared/Performance/PartsUserActivityTable";
import VehicleAcquisitionTable from "../../components/dashboard/VehicleAcquisitionTable";
import VehicleDismantlingListsTable from "../../components/dashboard/VehicleDismantlingListsTable";
import VehicleManagementTable from "../../components/dashboard/VehicleManagementTable";
import VehicleProcessingTable from "../../components/dashboard/VehicleProcessingTable";
import VehicleReferralsTable from "../../components/dashboard/VehicleReferralsTable";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StripeProductModal from "../../components/shared/Stripe/StripeProductModal";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import useDashboardStats from "../../data/useDashboardStats";
import { IDashboardStats } from "../../models/DashboardStats";
import { CheckoutSessionType } from "../../models/Stripe";
import PriceFormat from "../../utils/priceFormat";

type DashboardRangeKey = "today" | "week" | "month" | "last30" | "year";

const rangeTabs = [
  { name: "Today", value: "today" },
  { name: "Week", value: "week" },
  { name: "Month", value: "month" },
];

function numberValue(value: number | null | undefined): string {
  if (value == null) return "-";
  return value.toString();
}

function percentValue(value: number | null | undefined): string {
  if (value == null) return "-";
  return `${value.toFixed(2)}%`;
}

function getStats(
  ctx: DashboardMetricsTableContext<IDashboardStats>,
  range: DashboardRangeKey
): IDashboardStats | undefined {
  return ctx.statsByTimeframe[range];
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { claims, loggedIn, loading } = useAuth();
  const { setInfo } = usePageNameContext();
  const { features } = useCustomerDetails();
  const { todayStats, weekToDateStats, monthToDateStats, last30DaysStats, lastYearStats } =
    useDashboardStats();
  const [stripeModalOpen, setStripeModalOpen] = useState(false);

  const hasParts = useMemo(
    () => !!features.data?.data?.find((f) => f.name === "Parts Tracker"),
    [features.data]
  );

  const hasLeads = useMemo(
    () => !!features.data?.data?.find((f) => f.name === "Lead Management"),
    [features.data]
  );

  useEffect(() => {
    if (claims?.isDriver && !claims.isStaff && !claims.isAdmin) {
      navigate("/find-lead", { state: { from: location }, replace: true });
      return;
    }

    if (claims?.isExporter && !claims.isStaff && !claims.isAdmin) {
      navigate("/exports", { state: { from: location }, replace: true });
      return;
    }

    if (!claims?.isAdmin) {
      if (hasLeads) {
        navigate("/leads", { state: { from: location }, replace: true });
        return;
      }
      if (hasParts) {
        navigate("/vehicles", { state: { from: location }, replace: true });
        return;
      }
      navigate("/accounts", { state: { from: location }, replace: true });
    }
  }, [claims, hasLeads, hasParts, loading, location, navigate]);

  useEffect(() => {
    setInfo({
      name: "Dashboard",
      desc: "A quick glance at the current status of your business",
    });
  }, [setInfo]);

  const ready =
    !todayStats.isLoading &&
    !weekToDateStats.isLoading &&
    !monthToDateStats.isLoading &&
    !last30DaysStats.isLoading &&
    !lastYearStats.isLoading &&
    !!todayStats.data &&
    !!weekToDateStats.data &&
    !!monthToDateStats.data &&
    !!last30DaysStats.data &&
    !!lastYearStats.data &&
    !features.isLoading &&
    loggedIn &&
    !loading;

  if (!ready) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingWheel />
      </div>
    );
  }

  const today = todayStats.data!.data;
  const week = weekToDateStats.data!.data;
  const month = monthToDateStats.data!.data;
  const last30 = last30DaysStats.data!.data;
  const year = lastYearStats.data!.data;

  const dashboardStats: IDashboardStats = {
    ...today,
    vehiclesAwaitingCOD: year.vehiclesAwaitingCOD,
    vehiclesAwaitingID: year.vehiclesAwaitingID,
    vehiclesAwaitingPayment: year.vehiclesAwaitingPayment,
    vehiclesAwaitingPaymentValue: year.vehiclesAwaitingPaymentValue,
    vehiclesAwaitingPurchase: year.vehiclesAwaitingPurchase,
    vehiclesAwaitingPurchaseValue: year.vehiclesAwaitingPurchaseValue,
    vehiclePurchasedMTD: month.vehiclePurchasedMTD,
    vehiclePurchasedMTDValue: month.vehiclePurchasedMTDValue,
    vehiclesAwaitingCompletion: year.vehiclesAwaitingCompletion,
    failedPayments: year.failedPayments,
    unscheduledLeads: last30.unscheduledLeads,
    rescheduledLeads: last30.rescheduledLeads,
    percentageStartsDrivesRange: last30.percentageStartsDrivesRange,
    percentageStartsDrivesDriverRange: last30.percentageStartsDrivesDriverRange,
    vehiclesCollected: last30.vehiclesCollected,
    leadsBooked: last30.leadsBooked,
    leadsCompleted: last30.leadsCompleted,
    partsWithImages: last30.partsWithImages,
    partsRemoved: last30.partsRemoved,
    engineExportCount: last30.engineExportCount,
    engineExportPrice: last30.engineExportPrice,
    engineExportAvgPrice: last30.engineExportAvgPrice,
    vehicleReferrals: year.vehicleReferrals,
    monthlyIncomePerOutcode: year.monthlyIncomePerOutcode,
    monthlyBooked: year.monthlyBooked,
    monthlyCollected: year.monthlyCollected,
    sccConversionRate: year.sccConversionRate,
    ctbConversionRate: year.ctbConversionRate,
    mwConversionRate: year.mwConversionRate,
    sellAVehicleConversionRate: year.sellAVehicleConversionRate,
    totalPurchasesMonth: month.totalPurchasesMonth,
    averageCommissionMonth: month.averageCommissionMonth,
    partsSoldTotal: year.partsSoldTotal,
    partsSoldTotalValue: year.partsSoldTotalValue,
    unlistedStockCount: year.unlistedStockCount,
    unlistedStockValue: year.unlistedStockValue,
    listedStockCount: year.listedStockCount,
    listedStockValue: year.listedStockValue,
    totalVehiclesOnsite: year.totalVehiclesOnsite,
    vehiclesAwaitingDismantlingLists: year.vehiclesAwaitingDismantlingLists,
    vehiclesWithDismantlingLists: year.vehiclesWithDismantlingLists,
    totalPartsAwaitingRemoval: year.totalPartsAwaitingRemoval,
    totalEnginesAwaitingRemoval: year.totalEnginesAwaitingRemoval,
    unreadMessages: year.unreadMessages,
  };

  const statsByRange: Record<DashboardRangeKey, IDashboardStats> = {
    today,
    week,
    month,
    last30,
    year,
  };

  const userReminderRows: DashboardMetricsTableRow<IDashboardStats>[] = [
    {
      id: "unread-reminder",
      label: "Unread Messages",
      value: (ctx) => numberValue(getStats(ctx, "today")?.unreadMessages),
      bgClass: "bg-blue-50",
      textClass: "text-blue-700",
    },
    {
      id: "unscheduled-reminder",
      label: "Unscheduled Leads (Last 30 Days)",
      value: (ctx) => numberValue(getStats(ctx, "last30")?.unscheduledLeads),
      bgClass: "bg-amber-50",
      textClass: "text-amber-700",
    },
    {
      id: "rescheduled-reminder",
      label: "Leads To Reschedule (Last 30 Days)",
      value: (ctx) => numberValue(getStats(ctx, "last30")?.rescheduledLeads),
      bgClass: "bg-fuchsia-50",
      textClass: "text-fuchsia-700",
    },
    {
      id: "documents-reminder",
      label: "Documents Uploaded (Today)",
      value: (ctx) =>
        numberValue(getStats(ctx, "today")?.documentsUploadedByUniqueCustomersToday),
      bgClass: "bg-lime-50",
      textClass: "text-lime-700",
    },
  ];

  const topStats = [
    {
      id: "awaiting-arrival",
      label: "Awaiting Arrival",
      value: numberValue(
        Math.max(
          (today.collectionsToday ?? 0) - (today.userActivityTotal?.collected ?? 0),
          0
        )
      ),
    },
    { id: "leads-created-today", label: "Leads Created Today", value: numberValue(today.bookedToday) },
    { id: "job-created", label: "Job Created Today", value: numberValue(today.jobsCreated) },
    { id: "dismantled-today", label: "Dismantled Today", value: numberValue(today.vehicleDismantledToday) },
    // { id: "depolluted-today", label: "Depolluted Today", value: numberValue(today.vehiclesDepollutedToday) },
    // { id: "crushed-today", label: "Crushed Today", value: "-" },
    { id: "parts-removed-today", label: "Parts Removed Today", value: numberValue(today.partsRemovedToday) },
    { id: "engines-removed-today", label: "Engines Removed Today", value: numberValue(today.exportEnginesRemovedToday) },
    { id: "parts-sold-today", label: "Parts Sold Today", value: numberValue(today.partsSoldToday) },
    { id: "parts-sold-today-value", label: "Parts Sold Today Value", value: PriceFormat(today.partsSoldTodayPrice ?? 0) },
  ];

  return (
    <>
      <StripeProductModal
        open={stripeModalOpen}
        setOpen={setStripeModalOpen}
        mutation={null}
        sessionType={CheckoutSessionType.LookupToken}
      />

      {/* ----- SITE PRODUCTIVITY BAR (centred, above tables) ----- */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-8">
        {topStats.map((stat) => (
          <div key={stat.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <p className="text-xs font-medium text-gray-500">{stat.label}</p>
            <p className="mt-1 text-lg font-bold text-primary-700">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* ----- USER REMINDERS (above four tables) ----- */}
      {/* <div className="mt-4 grid grid-cols-1 gap-4">
        <DashboardMetricsTable
          title="User Reminders"
          subtitle="Priority actions and queue alerts"
          rowConfig={userReminderRows}
          statsByTimeframe={statsByRange}
          span={1}
        />
      </div> */}

      {/* ----- TOP ROW: FOUR TABLES ----- */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <VehicleManagementTable dashboardStats={dashboardStats} span={1} />
        <ComplianceTable dashboardStats={dashboardStats} span={1} />
        <VehicleDismantlingListsTable dashboardStats={dashboardStats} span={1} />
        <VehicleProcessingTable dashboardStats={dashboardStats} span={1} />
      </div>

      {/* ----- SECOND ROW: VEHICLE ACQUISITION + PARTS MANAGEMENT ----- */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <VehicleAcquisitionTable dashboardStats={dashboardStats} span={1} />
        <PartsManagementTable dashboardStats={dashboardStats} span={1} />
      </div>

      {hasParts ? (
        /* ----- FULL WIDTH: PERSONNEL ACTIVITY ----- */
        <div className="mt-4 grid grid-cols-1 gap-4">
          <PartsUserActivityTable
            todayStats={today}
            weekStats={week}
            monthStats={month}
            title="Personnel Activity"
            rowLabelColumn="User"
          />
        </div>
      ) : null}

      {/* ----- THIRD ROW: FINANCIAL + MATERIALS RECOVERED ----- */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <FinancialInformationTable dashboardStats={dashboardStats} span={1} />
        <MaterialsRecoveredTable dashboardStats={dashboardStats} span={1} />
      </div>

      {/* ----- FULL WIDTH: VEHICLE REFERRALS TABLE ----- */}
      <div className="mt-4 grid grid-cols-1 gap-4">
        <VehicleReferralsTable todayStats={today} weekStats={week} monthStats={month} />
      </div>
    </>
  );
}
